import { Details } from "~/entities/Details";

// BIK-2095: AVS Region mode requires a single-line string. A pasted multi-line address
// (e.g. company / street / city-state-zip on separate lines) must have its line breaks
// turned into spaces — otherwise the lines run together ("COMPOSITES59"), UPS can't parse
// it, and AVS returns no candidates. Also collapses repeated whitespace.
export function toSingleLine(text: string): string {
  return text.replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
}

// BIK-2134: strips the city/state/postal tail off a one-line paste, leaving just the street
// as the user typed it. The changed-address modal shows the entered street on its own line
// and the city/state/zip underneath, so passing the whole one-liner as the street repeats
// the city and state and reads as a malformed address.
//
// AVS's own answer is the guide: each trailing part it parsed out is removed from the end
// (postal, then state, then city). Only trailing matches are removed, so a street that
// legitimately repeats the city name ("123 NEW YORK AVE, NEW YORK NY") keeps its copy. If
// nothing can be stripped the original text is returned unchanged.
export function stripAddressTail(
  oneLine: string,
  parts: { city?: string; state?: string; postalCode?: string }
): string {
  let words = toSingleLine(oneLine)
    .replace(/,/g, " ")
    .split(" ")
    .filter(Boolean);

  const dropTrailing = (value?: string): void => {
    if (!value) return;
    const needle = value.trim().replace(/,/g, " ").split(/\s+/).filter(Boolean);
    if (!needle.length || needle.length > words.length) return;
    const start = words.length - needle.length;
    const matches = needle.every(
      (n, i) => words[start + i].toUpperCase() === n.toUpperCase()
    );
    if (matches) words = words.slice(0, start);
  };

  // BIK-2144: drop whatever trails the POSTAL CODE before the per-part matching below.
  //
  // Removal only matches at the end of the string and each step exposes the next one, so a
  // paste ending in the country ("… TX 78626-7730, US") defeated the entire chain: the postal
  // check compared against "US", failed, and every later step failed with it. The street then
  // kept the whole pasted line, differed from the street UPS returned, and the changed-address
  // modal opened for an address that had not changed.
  //
  // Matching the country by name is not viable — Google localizes it, so the same address can
  // arrive as "US", "USA", "United States" or "EE. UU." (two tokens, with periods). Chasing
  // spellings is the heuristic parsing BIK-2136/2137 had to undo.
  //
  // Anchor on the postal code instead: it is a value AVS gives us, and by definition nothing
  // after it belongs to the street. Only trailing tokens are removed, so a street that repeats
  // the ZIP keeps its copy, and a paste with no ZIP is left untouched for the steps below.
  const anchor = parts.postalCode?.trim();
  let anchorIndex = -1;
  if (anchor) {
    const candidates = [anchor.toUpperCase(), anchor.split("-")[0].toUpperCase()];
    // Last occurrence: a street number identical to the ZIP must not anchor the cut.
    for (let i = words.length - 1; i >= 0; i--) {
      if (candidates.includes(words[i].toUpperCase())) {
        anchorIndex = i;
        break;
      }
    }
  }
  // BIK-2148: when AVS CORRECTS the postal code, its answer is not in the pasted text at
  // all ("…TX 12345 US" answered with 78205-2601), so the lookup above finds nothing and
  // the street kept the whole line — the modal then showed the city/state/zip twice on the
  // "You Entered" side. Fall back to the postal code the USER wrote: match a ZIP-shaped
  // token instead of a literal value. Deliberately narrow — 5 digits, optional +4, or a
  // 5-digit token immediately followed by a "-1234" — so a street number cannot match it.
  // Searched from the end and never before index 1, so a house number is never the anchor.
  let anchorIsUserPostal = false;
  if (anchorIndex === -1) {
    const zipShape = /^\d{5}(-\d{4})?$/;
    for (let i = words.length - 1; i >= 1; i--) {
      if (zipShape.test(words[i])) {
        anchorIndex = i;
        anchorIsUserPostal = true;
        break;
      }
    }
  }
  if (anchorIndex >= 0) {
    // Drop the anchor token itself when it is the user's own postal code: the
    // dropTrailing calls below match against the AVS value, which by definition differs
    // here, so the pasted ZIP would survive and be printed twice in the modal.
    words = words.slice(0, anchorIsUserPostal ? anchorIndex : anchorIndex + 1);
  }
  // The pasted ZIP may be either the plain five or the +4 form, and AVS returns the +4 —
  // try both so the tail is recognized either way.
  const postal = parts.postalCode?.trim();
  if (postal) {
    dropTrailing(postal);
    dropTrailing(postal.split("-")[0]);
  }
  dropTrailing(parts.state);
  dropTrailing(parts.city);

  const street = words.join(" ").replace(/[\s,]+$/, "").trim();
  return street || toSingleLine(oneLine);
}

// BIK-2095: a one-line entry from the single main input — either pasted ("paste") or typed
// ("manual-single") — is verified via the UPS XAV "Region" mode. Segmented-field edits use
// "manual" and take the normal segmented path below.
export function isRegionEntry(stageDetail: Details): boolean {
  return (
    !!stageDetail?.rawInput &&
    (stageDetail?.inputMethod === "paste" ||
      stageDetail?.inputMethod === "manual-single")
  );
}

// BIK-2127: Region mode is a paste-direct (AVS_RAW_PASTE_DIRECT) mechanic, but isRegionEntry
// only looks at inputMethod/rawInput — and the legacy paste path sets BOTH of those for
// BIK-2097 telemetry before handing off to Google Places. So with the flag OFF a pasted
// address still reached AVS as `?region=<one-line>`, dropping the city/state/postal that
// /replace had just resolved (UPS then answered "No Avs Results Returned" for a valid
// address). Worse, avsCheck's own isRegionPaste DOES gate on the flag, so the seeding of
// state/country and the Region-specific failure classification were skipped — the two
// layers disagreed.
//
// `regionEnabled` lets the caller gate the decision at the same layer that builds the
// params. Required (no default) on purpose: Region is only ever correct where the address
// has NOT been broken into fields yet, so a new caller must decide deliberately rather
// than inherit it from an inputMethod set three steps earlier. Ship1 (avsCheck) passes the
// flag; Ship3's unit check passes false.
export default function (
  stageDetail: Details,
  regionEnabled: boolean
): string {
  // BIK-2095: send the one-line entry to the AVS endpoint via the `region` param (Region
  // mode). The backend ignores the segmented params in this mode, so we only send region +
  // country. country defaults to US (Region is US/PR only).
  if (regionEnabled && isRegionEntry(stageDetail)) {
    const country =
      typeof stageDetail.country === "string"
        ? stageDetail.country
        : stageDetail.country?.code || "US";
    return new URLSearchParams({
      // Safety net: normalize to single-line here too, so any rawInput source
      // (paste, typed, restored) is guaranteed line-break-free before hitting AVS.
      region: toSingleLine(stageDetail.rawInput as string),
      country,
    }).toString();
  }
  const data = {
    company: stageDetail?.company ?? "",
    address1: stageDetail.streetAddress,
    address2: stageDetail?.address2 ?? "",
    city: stageDetail.city ?? "",
    state:
      typeof stageDetail.state === "string"
        ? stageDetail.state
        : stageDetail.state?.stateprovcode ?? "",
    postalCode: stageDetail.postalCode,
    country:
      typeof stageDetail.country === "string"
        ? stageDetail.country
        : stageDetail.country?.code ?? "",
  };
  const searchParams = new URLSearchParams(data);
  return searchParams.toString();
}
